import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';

export interface BookableZone {
  id: string;
  establishmentId: string;
  name: string;
  slug: string;
  color: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UseBookableZonesOptions {
  establishmentId?: string | null;
  /** Use authenticated client (staff side) vs anon public client (/book) */
  useAuthenticatedData?: boolean;
}

export function bookableZonesQueryKey(establishmentId?: string | null) {
  return ['bookable-zones', establishmentId ?? null] as const;
}

/**
 * Slugify a name into a stable ASCII slug.
 * Lowercase, strip diacritics, replace non-alphanumeric with '-'.
 */
export function slugifyZoneName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'zone';
}

const mapRow = (row: any): BookableZone => ({
  id: row.id,
  establishmentId: row.establishment_id,
  name: row.name,
  slug: row.slug,
  color: row.color ?? '#6366f1',
  displayOrder: row.display_order ?? 0,
  isActive: row.is_active ?? true,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const useBookableZones = (options: UseBookableZonesOptions = {}) => {
  const { establishmentId, useAuthenticatedData = true } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: bookableZonesQueryKey(establishmentId),
    queryFn: async (): Promise<BookableZone[]> => {
      if (!establishmentId) return [];
      const client = useAuthenticatedData ? supabase : supabasePublic;
      const { data, error } = await client
        .from('bookable_zones')
        .select('*')
        .eq('establishment_id', establishmentId)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return (data || []).map(mapRow);
    },
    enabled: Boolean(establishmentId),
    staleTime: 1000 * 60 * 2,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: bookableZonesQueryKey(establishmentId) });

  const ensureUniqueSlug = async (
    baseSlug: string,
    excludeId?: string
  ): Promise<string> => {
    if (!establishmentId) return baseSlug;
    const existing = (query.data || []).filter(z => z.id !== excludeId);
    let slug = baseSlug;
    let suffix = 2;
    while (existing.some(z => z.slug === slug)) {
      slug = `${baseSlug}-${suffix}`;
      suffix++;
    }
    return slug;
  };

  const createZone = useMutation({
    mutationFn: async (params: {
      name: string;
      color?: string;
      displayOrder?: number;
    }) => {
      if (!establishmentId) throw new Error('No establishment');
      const baseSlug = slugifyZoneName(params.name);
      const slug = await ensureUniqueSlug(baseSlug);
      const { data, error } = await supabase
        .from('bookable_zones')
        .insert({
          establishment_id: establishmentId,
          name: params.name.trim(),
          slug,
          color: params.color ?? '#6366f1',
          display_order: params.displayOrder ?? (query.data?.length ?? 0),
          is_active: true,
        })
        .select()
        .single();
      if (error) throw error;
      return mapRow(data);
    },
    onSuccess: invalidate,
  });

  const updateZone = useMutation({
    mutationFn: async (params: {
      id: string;
      name?: string;
      color?: string;
      displayOrder?: number;
      isActive?: boolean;
    }) => {
      const updates: Record<string, unknown> = {};
      if (params.name !== undefined) {
        updates.name = params.name.trim();
        const baseSlug = slugifyZoneName(params.name);
        updates.slug = await ensureUniqueSlug(baseSlug, params.id);
      }
      if (params.color !== undefined) updates.color = params.color;
      if (params.displayOrder !== undefined) updates.display_order = params.displayOrder;
      if (params.isActive !== undefined) updates.is_active = params.isActive;

      const { error } = await supabase
        .from('bookable_zones')
        .update(updates)
        .eq('id', params.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteZone = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bookable_zones')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    zones: query.data || [],
    loading: query.isLoading,
    error: query.error,
    createZone,
    updateZone,
    deleteZone,
    refetch: query.refetch,
  };
};
